import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileDown } from "lucide-react";
import { api, errMsg, openBlob, todayIso, ORDER_STATUS, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, DataTable, DirectoryPicker, Field, LinesEditor, PageHeader, Select, cleanLines, emptyLine, validLines } from "@/components/common";
import Modal from "@/components/Modal";

export default function Orders() {
  const { user, settings } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [sups, setSups] = useState([]);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  const editable = can(user, "createur");
  const load = () => api.get("/orders").then((r) => { setRows(r.data); const o = r.data.find((x) => x.id === params.get("id")); if (o) setForm(o); });
  useEffect(() => { load(); api.get("/suppliers").then((r) => setSups(r.data)); }, []); // eslint-disable-line
  const name = (id) => sups.find((s) => s.id === id)?.name || "";
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    if (!form.supplier_id || !form.cost_center || !validLines(form.lines)) return setErr("Fournisseur, centre de coût et lignes valides (désignation, quantité > 0, prix ≥ 0) sont obligatoires");
    try {
      const body = { ...form, lines: cleanLines(form.lines) };
      form.id ? await api.put(`/orders/${form.id}`, body) : await api.post("/orders", body);
      toast.success("Bon de commande enregistré");
      setForm(null);
      load();
    } catch (x) { setErr(errMsg(x)); }
  };
  const columns = [
    { key: "number", label: "N° commande", render: (r) => <span className="mono font-semibold">{r.number}</span> },
    { key: "supplier", label: "Fournisseur", value: (r) => name(r.supplier_id) },
    { key: "date", label: "Date", type: "date" },
    { key: "requester_name", label: "Demandeur" },
    { key: "cost_center", label: "Centre de coût" },
    { key: "status", label: "Statut", value: (r) => ORDER_STATUS[r.status][0], render: (r) => <Badge meta={ORDER_STATUS[r.status]} /> },
    { key: "total_ht", label: "Total HT", type: "money" },
    { key: "total_ttc", label: "Total TTC", type: "money" },
    { key: "pdf", label: "PDF", export: false, sortable: false, render: (r) => <button className="btn-ghost" onClick={(e) => { e.stopPropagation(); openBlob(`/orders/${r.id}/pdf`); }} data-testid={`order-pdf-btn-${r.number}`}><FileDown size={15} /></button> },
  ];
  return (
    <div>
      <PageHeader title="Bons de commande" subtitle="Numérotation automatique BC-AAAA-0001, totaux calculés, PDF aux couleurs de la charte.">
        {editable && <button className="btn-primary" onClick={() => { setErr(""); setForm({ supplier_id: "", date: todayIso(), requester_name: "", requester_email: "", cost_center: "", lines: [emptyLine()], status: "brouillon", notes: "" }); }} data-testid="new-order-btn"><Plus size={16} /> Nouvelle commande</button>}
      </PageHeader>
      <DataTable columns={columns} rows={rows.filter((r) => !status || r.status === status)} title="Commandes" testid="orders-table" onRowClick={(r) => { setErr(""); setForm(r); }}
        filters={<Select className="!w-48" value={status} onChange={setStatus} placeholder="Tous statuts" options={Object.entries(ORDER_STATUS).map(([value, [label]]) => ({ value, label }))} testid="orders-status-filter" />} />
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.number || "Nouveau bon de commande"} wide testid="order-modal">
        {form && (
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Fournisseur *"><Select value={form.supplier_id} onChange={(v) => set("supplier_id", v)} placeholder="— Choisir —" options={sups.map((s) => ({ value: s.id, label: s.name }))} testid="order-supplier-select" /></Field>
              <Field label="Date"><input type="date" className="inp" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="order-date-input" /></Field>
              <Field label="Statut"><Select value={form.status} onChange={(v) => set("status", v)} options={Object.entries(ORDER_STATUS).map(([value, [label]]) => ({ value, label }))} testid="order-status-select" /></Field>
              <Field label="Demandeur (annuaire Microsoft 365)"><DirectoryPicker value={form.requester_name} onSelect={(p) => setForm({ ...form, requester_name: p.name, requester_email: p.email })} testid="order-requester-picker" /></Field>
              <Field label="Centre de coût *"><Select value={form.cost_center} onChange={(v) => set("cost_center", v)} placeholder="— Choisir —" options={(settings?.my_cost_centers || []).map((c) => ({ value: c.code, label: `${c.code} – ${c.label}` }))} testid="order-cost-center-select" /></Field>
              <Field label="Notes"><input className="inp" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <LinesEditor lines={form.lines} onChange={(l) => set("lines", l)} readOnly={!editable} testid="order-lines" />
            {err && <p className="text-sm font-semibold text-[#B03A3A]" data-testid="order-error">{err}</p>}
            <div className="flex justify-end gap-2">
              {form.id && <button type="button" className="btn-secondary" onClick={() => openBlob(`/orders/${form.id}/pdf`)} data-testid="order-modal-pdf-btn"><FileDown size={15} /> PDF</button>}
              {editable && <button className="btn-primary" data-testid="order-save-btn">Enregistrer</button>}
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
